import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as authService from '../services/authService';
import { generatePkcePair } from '../utils/pkce';

const initialState = {
  accessToken: null,
  hasMfa: false,
  profile: null,
  hasRefreshCookie: false,
  hasPendingMfaSetup: false,
  accountEmail: null,
  credentialId: null,
};

const AuthContext = createContext(undefined);

const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded =
      normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('No se pudo decodificar el accessToken', error);
    return null;
  }
};

const createLimiter = (maxAttempts, windowMs) => {
  const attempts = [];
  return () => {
    const now = Date.now();
    while (attempts.length && attempts[0] <= now - windowMs) {
      attempts.shift();
    }
    if (attempts.length >= maxAttempts) {
      const retryIn = windowMs - (now - attempts[0]);
      return { allowed: false, retryIn };
    }
    attempts.push(now);
    return { allowed: true, retryIn: 0 };
  };
};

const loginLimiter = createLimiter(5, 60_000);
const registerLimiter = createLimiter(3, 60_000);
const mfaLimiter = createLimiter(10, 60_000);

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const [pendingMfa, setPendingMfa] = useState(false);
  const pendingRef = useRef(null);
  const loginEmailRef = useRef(null);
  const loggingInRef = useRef(false);

  const clearState = useCallback(() => {
    pendingRef.current = null;
    loginEmailRef.current = null;
    loggingInRef.current = false;
    setPendingMfa(false);
    setState(initialState);
  }, []);

  const applyTokens = useCallback((tokens, hasRefreshCookie = true, knownEmail = null) => {
    if (!tokens?.accessToken) {
      throw new Error('AccessToken inválido recibido del servicio de autenticación');
    }
    const profile = decodeJwt(tokens.accessToken);
    setState((prev) => ({
      accessToken: tokens.accessToken,
      hasMfa: tokens.hasMfa ?? prev.hasMfa ?? false,
      profile,
      hasRefreshCookie: hasRefreshCookie ?? prev.hasRefreshCookie,
      hasPendingMfaSetup: false,
      accountEmail: knownEmail ?? prev.accountEmail ?? null,
      credentialId: profile?.sub ?? prev.credentialId ?? null,
    }));
    return tokens.accessToken;
  }, []);

  const enforceLimiter = useCallback((kind) => {
    const limiter =
      kind === 'register'
        ? registerLimiter
        : kind === 'mfa'
        ? mfaLimiter
        : loginLimiter;
    const result = limiter();
    if (!result.allowed) {
      const seconds = Math.ceil(result.retryIn / 1000);
      throw new Error(
        `Demasiados intentos. Intenta nuevamente en ${seconds} segundo${seconds !== 1 ? 's' : ''}.`,
      );
    }
  }, []);

  const register = useCallback(
    async ({
      correoElectronico,
      password,
      primerNombre,
      primerApellido,
      segundoNombre,
      segundoApellido,
    }) => {
      enforceLimiter('register');
      await authService.register({
        correoElectronico,
        password,
        primerNombre,
        primerApellido,
        segundoNombre,
        segundoApellido,
      });
    },
    [enforceLimiter],
  );

  const login = useCallback(
    async (correoElectronico, password) => {
      enforceLimiter('login');
      loggingInRef.current = true;
      loginEmailRef.current = correoElectronico;
      const { verifier, challenge } = await generatePkcePair();
      const { code, hasMfa } = await authService.login({
        correoElectronico,
        password,
        codeChallenge: challenge,
      });
      pendingRef.current = { code, verifier, email: correoElectronico };
      if (hasMfa) {
        setPendingMfa(true);
        return { requiresMfa: true };
      }
      const tokens = await authService.exchange({
        code,
        codeVerifier: verifier,
      });
      pendingRef.current = null;
      setPendingMfa(false);
      applyTokens(tokens, true, correoElectronico);
      loginEmailRef.current = null;
      loggingInRef.current = false;
      return { requiresMfa: false };
    },
    [applyTokens, enforceLimiter],
  );

  const verifyMfa = useCallback(
    async (totp) => {
      enforceLimiter('mfa');
      if (!pendingRef.current) {
        throw new Error('No existe un inicio de sesión pendiente de MFA');
      }
      const { code, verifier, email } = pendingRef.current;
      await authService.verifyMfa(code, totp);
      const tokens = await authService.exchange({
        code,
        codeVerifier: verifier,
      });
      pendingRef.current = null;
      setPendingMfa(false);
      const knownEmail = email ?? loginEmailRef.current ?? state.accountEmail ?? null;
      applyTokens(tokens, true, knownEmail);
      loginEmailRef.current = null;
      loggingInRef.current = false;
    },
    [applyTokens, enforceLimiter, state.accountEmail],
  );

  const refreshTokens = useCallback(async () => {
    if (!state.hasRefreshCookie) {
      throw new Error('La sesión no admite renovación automática');
    }
    const tokens = await authService.refresh();
    return applyTokens({ ...tokens, hasMfa: state.hasMfa }, true, state.accountEmail ?? null);
  }, [applyTokens, state.accountEmail, state.hasMfa, state.hasRefreshCookie]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('No se pudo cerrar sesión en el servicio', error);
    } finally {
      clearState();
    }
  }, [clearState]);

  const enableMfa = useCallback(async () => {
    if (!state.hasRefreshCookie) {
      throw new Error('Necesitas iniciar sesión para configurar MFA');
    }
    const result = await authService.enableMfa();
    setState((prev) => ({
      ...prev,
      hasPendingMfaSetup: true,
    }));
    return result;
  }, [state.hasRefreshCookie]);

  const confirmMfaSetup = useCallback(
    async (code) => {
      enforceLimiter('mfa');
      if (!state.hasRefreshCookie) {
        throw new Error('Necesitas iniciar sesión para configurar MFA');
      }
      await authService.confirmMfa(code);
      setState((prev) => ({
        ...prev,
        hasMfa: true,
        hasPendingMfaSetup: false,
      }));
    },
    [enforceLimiter, state.hasRefreshCookie],
  );

  const cancelMfaSetup = useCallback(async () => {
    if (!state.hasRefreshCookie) {
      throw new Error('Necesitas iniciar sesión para configurar MFA');
    }
    await authService.cancelMfa();
    setState((prev) => ({
      ...prev,
      hasPendingMfaSetup: false,
    }));
  }, [state.hasRefreshCookie]);

  const cancelPendingMfaChallenge = useCallback(() => {
    pendingRef.current = null;
    setPendingMfa(false);
  }, []);

  const authFetch = useCallback(
    async (input, init = {}, retry = false) => {
      if (!state.accessToken) {
        throw new Error('No hay accessToken disponible');
      }
      const headers = new Headers(init.headers || {});
      headers.set('Authorization', `Bearer ${state.accessToken}`);
      const response = await fetch(input, { ...init, headers });

      if (response.status !== 401 || retry || !state.hasRefreshCookie) {
        return response;
      }

      try {
        const newAccessToken = await refreshTokens();
        const retryHeaders = new Headers(init.headers || {});
        retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);
        return fetch(input, { ...init, headers: retryHeaders });
      } catch (error) {
        clearState();
        throw error;
      }
    },
    [clearState, refreshTokens, state.accessToken, state.hasRefreshCookie],
  );

  const value = useMemo(
    () => ({
      accessToken: state.accessToken,
      refreshToken: null,
      profile: state.profile,
      hasMfa: state.hasMfa,
      isAuthenticated: !!state.accessToken,
      register,
      login,
      logout,
      verifyMfa,
      refresh: refreshTokens,
      enableMfa,
      confirmMfaSetup,
      cancelMfaSetup,
      authFetch,
      hasPendingMfa: pendingMfa,
      hasRefreshCookie: state.hasRefreshCookie,
      cancelPendingMfa: cancelPendingMfaChallenge,
      hasPendingMfaSetup: state.hasPendingMfaSetup,
      accountEmail: state.accountEmail,
    }),
    [
      authFetch,
      enableMfa,
      confirmMfaSetup,
      cancelMfaSetup,
      cancelPendingMfaChallenge,
      login,
      logout,
      pendingMfa,
      refreshTokens,
      register,
      state.accessToken,
      state.hasMfa,
      state.hasPendingMfaSetup,
      state.hasRefreshCookie,
      state.accountEmail,
      state.profile,
      verifyMfa,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuthContext debe utilizarse dentro de un AuthProvider');
  }
  return ctx;
};

export default AuthContext;
